// TradingViewWidget.jsx
import React, { useEffect, useRef, memo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

// Fixed container ID to avoid regenerating IDs
const CONTAINER_ID = "tradingview_chart_container";

function TradingViewWidget() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  
  // Map our app language codes to TradingView supported locale codes
  const getLocale = () => {
    switch (language) {
      case 'vi': return 'vi_VN';
      case 'zh': return 'zh_CN';
      case 'ko': return 'ko';
      default: return 'en';
    }
  };

  useEffect(() => {
    let scriptElement: HTMLScriptElement | null = null;
    
    const loadWidget = () => {
      // Remove any global TradingView objects that might be lingering
      if ((window as any).TradingView) {
        delete (window as any).TradingView;
      }
      
      if (!containerRef.current) return;
      
      // Clear container
      containerRef.current.innerHTML = '';
      
      // Create script
      scriptElement = document.createElement('script');
      scriptElement.type = 'text/javascript';
      scriptElement.async = true;
      scriptElement.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      
      // Set widget options
      scriptElement.innerHTML = JSON.stringify({
        autosize: true,
        symbol: "OKX:PIUSDT",
        interval: "60",
        timezone: "Etc/UTC",
        theme: isDarkMode ? "dark" : "light",
        style: "1",
        locale: getLocale(),
        allow_symbol_change: false,
        calendar: false,
        support_host: "https://www.tradingview.com",
        hide_top_toolbar: false,
        enable_publishing: false,
        hide_legend: true,
        save_image: false,
        container_id: CONTAINER_ID
      });
      
      // Add the script
      containerRef.current.appendChild(scriptElement);
    };
    
    // Use setTimeout to ensure clean DOM operations
    const timeoutId = setTimeout(() => {
      loadWidget();
    }, 100);
    
    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      
      // Remove script element if it exists
      if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
      
      // Clear container
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [isDarkMode, language]); // Track both theme and language changes

  return (
    <div 
      className="trading-view-chart"
      style={{ 
        width: '100%', 
        height: '525px', 
        backgroundColor: isDarkMode ? '#131722' : '#ffffff'
      }}
    >
      <div 
        id={CONTAINER_ID} 
        ref={containerRef}
        style={{ width: '100%', height: '100%' }} 
      />
    </div>
  );
}

// Use memo to prevent unnecessary re-renders
export default memo(TradingViewWidget);
